import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import { TranslocoModule } from '@ngneat/transloco';
import { ButtonModule } from '@syncfusion/ej2-angular-buttons';
import { DialogModule } from '@syncfusion/ej2-angular-popups';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { EmployeeHintsService } from './employee-hints.service';
import { AddressHint, ChildHint, HintItem, HintKind, PersonHint } from './hint.model';
import { hintsToItems } from './hint.utils';

/**
 * A small "fill from history" button that, when the employee has previously entered data of the
 * given `kind`, opens a picker dialog and emits the chosen hint. It renders nothing when there is
 * no history to offer, so hosts can drop it next to any section unconditionally.
 *
 * Standalone so it can be used from the employee-card formly types, the employee-guide steps and
 * the annual-tax-settlement standalone form parts alike.
 */
@Component({
  standalone: true,
  selector: 'tpa-hint-fill-button',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslocoModule, DialogModule, ButtonModule],
  template: `
    <ng-container *ngIf="items$ | async as items">
      <button
        *ngIf="items.length"
        type="button"
        class="e-btn e-lib e-small e-flat tpa-hint-fill__trigger"
        (click)="openDialog()"
      >
        <i class="fal fa-history"></i>
        <span>{{ label || 'TPA.FillFromHistory' | transloco }}</span>
      </button>

      <ejs-dialog
        *ngIf="open$ | async"
        [isModal]="true"
        [visible]="true"
        [showCloseIcon]="true"
        (overlayClick)="close()"
        (close)="close()"
        width="520px"
      >
        <ng-template #content>
          <div class="tpa-hint-fill__list">
            <h2 class="h2--big">{{ titleKey | transloco }}</h2>
            <button
              *ngFor="let item of items"
              type="button"
              class="tpa-hint-fill__row"
              (click)="select(item)"
            >
              <span class="tpa-hint-fill__row-title">{{ item.title }}</span>
              <span class="tpa-hint-fill__row-subtitle" *ngIf="item.subtitle">
                {{ item.subtitle }}
              </span>
            </button>
          </div>
        </ng-template>
        <ng-template #footerTemplate>
          <button (click)="close()" class="e-btn e-lib e-secondary" type="button">
            {{ 'TPA.Cancel' | transloco }}
          </button>
        </ng-template>
      </ejs-dialog>
    </ng-container>
  `,
  styles: [
    `
      :host {
        display: block;
      }
      .tpa-hint-fill__trigger {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 4px 10px;
        margin: 2px 0;
      }
      .tpa-hint-fill__trigger i {
        font-size: 12px;
      }
      .tpa-hint-fill__list {
        display: flex;
        flex-direction: column;
        gap: 8px;
        max-height: 60vh;
        overflow: auto;
      }
      .tpa-hint-fill__row {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        text-align: left;
        width: 100%;
        padding: 10px 12px;
        border: 1px solid var(--surface-d, #dee2e6);
        border-radius: 6px;
        background: var(--surface-a, #fff);
        cursor: pointer;
      }
      .tpa-hint-fill__row:hover {
        border-color: var(--primary-color, #2f6fed);
        background: var(--surface-b, #f6f8fb);
      }
      .tpa-hint-fill__row-title {
        font-weight: 600;
      }
      .tpa-hint-fill__row-subtitle {
        font-size: 0.85em;
        opacity: 0.75;
      }
    `,
  ],
})
export class HintFillButtonComponent implements OnInit, OnChanges {
  /** Which section this button fills. */
  @Input() kind!: HintKind;
  /** The employee whose history to offer. Omit on self-service surfaces (defaults to current user). */
  @Input() ownerId?: number | null;
  /** Optional override for the trigger label (Transloco key). */
  @Input() label?: string;

  /** Emits the raw hint the user picked. */
  @Output() pick = new EventEmitter<AddressHint | ChildHint | PersonHint>();

  readonly open$ = new BehaviorSubject<boolean>(false);
  private readonly kind$ = new BehaviorSubject<HintKind | undefined>(undefined);
  private readonly ownerId$ = new BehaviorSubject<number | null | undefined>(undefined);
  /** Ticks whenever the history must be re-read: on first render and on every dialog open. */
  private readonly reload$ = new BehaviorSubject<void>(undefined);

  items$!: Observable<HintItem[]>;

  private static readonly TITLE_KEYS: Record<HintKind, string> = {
    address: 'TPA.FillFromHistory_AddressTitle',
    child: 'TPA.FillFromHistory_ChildTitle',
    person: 'TPA.FillFromHistory_PersonTitle',
  };

  constructor(private readonly hints: EmployeeHintsService) {}

  get titleKey(): string {
    return HintFillButtonComponent.TITLE_KEYS[this.kind] ?? 'TPA.FillFromHistory';
  }

  ngOnInit(): void {
    this.items$ = combineLatest([this.kind$, this.ownerId$, this.reload$]).pipe(
      switchMap(([kind, ownerId]) =>
        this.hints.getHints(ownerId).pipe(map((hints) => hintsToItems(kind as HintKind, hints)))
      )
    );
  }

  ngOnChanges(): void {
    this.kind$.next(this.kind);
    this.ownerId$.next(this.ownerId);
  }

  /**
   * Re-reads the history before showing the picker, so a dialog opened later in the visit lists what
   * the employee has entered *now* — the previously rendered list is kept on screen until it lands.
   */
  openDialog(): void {
    this.reload$.next();
    this.open$.next(true);
  }

  close(): void {
    this.open$.next(false);
  }

  select(item: HintItem): void {
    this.pick.emit(item.raw);
    this.close();
  }
}
